import { createRef, useState } from 'react'
import { Dropdown, InputBox, TextArea } from './forms'
import {
  Button,
  Variant as ButtonVariant,
  Size as ButtonSize,
  Icon as ButtonIcon,
} from './Button'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  ContactFormValidationSchema,
  enquiryReasons,
  FieldNames,
} from './ContactFormValidationSchema'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { Captcha } from './forms/Captcha'

const FORM_NAME = 'ContactUsForm'
const TEXT_AREA_ROW_COUNT = 5

export function ContactForm() {
  const { t: translate } = useTranslation()
  const [isSubmitted, setSubmitted] = useState(false)
  const [pauseSubmit, setPauseSubmit] = useState(false)
  const captchaRef = createRef<HCaptcha>()
  const [errorSubmitting, setErrorSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Record<FieldNames, string>>({
    resolver: yupResolver(ContactFormValidationSchema),
  })

  async function submitMessage(data: any) {
    setPauseSubmit(true)
    const res = await fetch(
      `https://powcontactus.azurewebsites.net/api/ContactUs`,
      {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    if (res.status !== 200) {
      setErrorSubmitting(true)
    } else {
      setSubmitted(true)
    }
    setPauseSubmit(false)
  }

  return (
    <div className="flex justify-center pb-4">
      <form
        id={FORM_NAME}
        name={FORM_NAME}
        onSubmit={handleSubmit((data) => {
          captchaRef?.current?.execute()
          submitMessage(data).then(() => captchaRef?.current?.resetCaptcha())
        })}
        className="bg-white flex flex-col gap-y-6 max-w-916px px-10 py-6 shadow-lg w-full"
        onChange={() => setErrorSubmitting(false)}
      >
        <div className="flex flex-col gap-y-6 w-full md:flex-row md:gap-x-8">
          <InputBox
            id={FieldNames.firstName}
            label={translate(FieldNames.firstName)}
            register={register(FieldNames.firstName)}
            className="max-w-300px w-full md:max-w-full"
            required
            errorMessage={errors?.firstName?.message}
          />
          <InputBox
            id={FieldNames.lastName}
            label={translate(FieldNames.lastName)}
            register={register(FieldNames.lastName)}
            className="max-w-300px w-full md:max-w-full"
            required
            errorMessage={errors?.lastName?.message}
          />
        </div>
        <div className="flex flex-col gap-y-6 w-full md:flex-row md:gap-x-8">
          <InputBox
            id={FieldNames.emailAddress}
            label={translate(FieldNames.emailAddress)}
            register={register(FieldNames.emailAddress)}
            className="max-w-450px w-full md:max-w-full"
            required
            errorMessage={errors?.emailAddress?.message}
          />
          <InputBox
            id={FieldNames.phoneNumber}
            label={translate(FieldNames.phoneNumber)}
            register={register(FieldNames.phoneNumber)}
            className="max-w-350px w-full md:max-w-full"
          />
        </div>
        <Dropdown
          id={FieldNames.enquiryReason}
          label={translate(FieldNames.enquiryReason)}
          register={register(FieldNames.enquiryReason)}
          className="450px w-full md:max-w-full md:pr-4 md:w-1/2"
        >
          {enquiryReasons.map((reason) => (
            <option key={reason} value={reason}>
              {translate(reason)}
            </option>
          ))}
        </Dropdown>
        <TextArea
          id="messageBox"
          label={translate('message')}
          register={register('messageBody')}
          required
          errorMessage={errors?.messageBody?.message}
          rows={TEXT_AREA_ROW_COUNT}
        />
        <Captcha
          onVerify={(token: string) => {
            setValue(FieldNames.captchaToken, token, {
              shouldValidate: true,
            })
          }}
          errorMessage={errors?.captchaToken?.message}
        />
        <span>
          <Button
            translationKey="submit"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.STANDARD}
            icon={ButtonIcon.NONE}
            disabled={isSubmitted || pauseSubmit}
            type="submit"
          />
        </span>
        {!!isSubmitted && <span>{translate('thanksForMessage')}</span>}
        {!!errorSubmitting && (
          <span>{translate('errorSubmittingMessage')}</span>
        )}
      </form>
    </div>
  )
}
